import { intersectionBy, sortBy } from 'lodash';
import Vue from 'vue';
import Vuex from 'vuex';
import axiosInstance from '@/axios-instance';

Vue.use(Vuex);

const initialState = () => ({
  manufacturers: [],
  setElements: [],
  stationManufacturerId: NaN,
  statuses: [],
  user: null,
});

export function getStatusByName(state, name) {
  return state.statuses.find(status => status.name === name);
}

export function getManufacturer(state, id) {
  return state.manufacturers
    .find(manufacturer => manufacturer.id === id);
}

export function getManufacturerStatuses(state, manufacturerId) {
  const { statuses } = state;
  const manufacturer = getManufacturer(state, manufacturerId);

  if (!manufacturer) return [];

  return intersectionBy(statuses, manufacturer.statuses, 'id');
}

export default new Vuex.Store({
  strict: true,
  state: initialState(),
  getters: {
    userStatuses({ statuses, user }) {
      if (!user) return [];

      return user.statuses
        .map(userStatus => statuses.find(status => status.id === userStatus.id))
        .filter(status => status)
        .sort((lhs, rhs) => lhs.position - rhs.position);
    },
    isSystemAdmin({ user }) {
      if (!user) return false;
      return user.role === 'system-admin';
    },
    isCommissioner({ user }) {
      if (!user) return false;
      return ['system-admin', 'order-manager'].includes(user.role);
    },
    isManufacturingAdmin({ user }) {
      if (!user) return false;
      return user.role === 'manufacturing-admin';
    },
  },
  mutations: {
    reset(state) {
      Object.entries(initialState())
        .forEach(([key, value]) => {
          state[key] = value; // eslint-disable-line no-param-reassign
        });
    },
    set(state, changes) {
      Object.entries(changes)
        .forEach(([key, value]) => {
          Vue.set(state, key, value);
        });
    },
  },
  actions: {
    async logIn({ commit }, { credentials: { username, password } }) {
      const { data: { user } } = await axiosInstance.post('/authentication', null, {
        auth: {
          username,
          password,
        },
      });

      commit('set', { user });
    },
    async logOut({ commit }) {
      await axiosInstance.delete('/authentication');

      commit('reset');
    },
    async loadUser({ commit }) {
      const { data: { user } } = await axiosInstance.get('/authentication');
      commit('set', { user });
    },
    async loadResources({ dispatch }) {
      await Promise.all([
        dispatch('loadSetElements'),
        dispatch('loadManufacturers')
          .then(() => dispatch('loadStatuses')),
      ]);
    },
    async loadSetElements({ commit }) {
      const { data: setElements } = await axiosInstance.get('/set-elements', {
        params: { filter: { fields: { id: true, set: true, name: true } } },
      });

      commit('set', { setElements });
    },
    async loadManufacturers({ commit }) {
      const { data: manufacturers } = await axiosInstance.get('/manufacturers', {
        params: { filter: { include: [{ relation: 'statuses' }] } },
      });

      commit('set', { manufacturers });
    },
    async loadStatuses({ commit, state, getters }) {
      let statuses;
      if (getters.isCommissioner) {
        ({ data: statuses } = await axiosInstance.get('/statuses'));
      } else {
        const { manufacturerId } = state.user;

        statuses = state.manufacturers
          .find(m => m.id === manufacturerId)
          .statuses
          .filter(status => !status.restricted);
      }

      commit('set', { statuses: sortBy(statuses, 'position') });
    },
  },
});
